import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, XCircle, Compass } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2500;

export const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [attempt, setAttempt] = useState(0);
  const [data, setData] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setData(res.data);
        if (res.data.payment_status === "paid" || res.data.status === "completed") {
          setStatus("paid");
          return;
        }
        if (n >= MAX_ATTEMPTS) {
          setStatus("expired");
          return;
        }
        setAttempt(n + 1);
        timerRef.current = setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus("failed");
      }
    };

    poll(0);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // MAX_ATTEMPTS/POLL_INTERVAL_MS são constantes de módulo; setState são estáveis.
    // Apenas sessionId (route param) reinicia o polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#080C14] text-[#F8FAFC] flex flex-col items-center justify-center px-4 font-sans">
      <div className="max-w-lg w-full text-center">
        <button
          data-testid="success-brand-link"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-3 mb-10 group"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B6914] flex items-center justify-center shadow-lg">
            <Compass className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
          </div>
          <div className="text-left">
            <div className="text-sm font-serif font-bold text-white leading-tight">Lojas Integradas</div>
            <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Gestão Maçônica</div>
          </div>
        </button>

        {status === "checking" && (
          <div data-testid="payment-status-checking" className="rounded-2xl bg-[#0d1420] border border-slate-800 p-10">
            <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto" />
            <h1 className="mt-6 text-2xl font-serif font-bold text-white">Confirmando seu pagamento…</h1>
            <p className="mt-3 text-sm text-slate-400">
              Estamos verificando a transação junto ao Stripe. Isso leva alguns segundos.
            </p>
            <p className="mt-4 text-[11px] text-slate-600 font-mono">Tentativa {attempt + 1} / {MAX_ATTEMPTS + 1}</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-status-paid" className="rounded-2xl bg-[#0d1420] border border-emerald-700/40 p-10">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" strokeWidth={2} />
            </div>
            <h1 className="mt-6 text-3xl font-serif font-bold text-white">Assinatura ativada!</h1>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Bem-vindo(a) à jornada Lojas Integradas.
              <br />Sua Loja já está com o plano ativo e todos os recursos liberados.
            </p>
            {data?.amount != null && (
              <div className="mt-6 inline-flex flex-col rounded-xl bg-slate-900/60 border border-slate-800 px-6 py-3 text-left">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Total pago</span>
                <span className="text-xl font-mono font-bold text-[#D4AF37]">
                  {(data.currency || "brl").toUpperCase()} {Number(data.amount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <button
              data-testid="success-go-dashboard"
              onClick={() => navigate("/")}
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold uppercase tracking-wider text-sm rounded-xl shadow-lg transition-all"
            >
              Ir para o painel <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {status === "expired" && (
          <div data-testid="payment-status-expired" className="rounded-2xl bg-[#0d1420] border border-amber-700/40 p-10">
            <Loader2 className="w-16 h-16 text-amber-400 mx-auto" />
            <h1 className="mt-6 text-2xl font-serif font-bold text-white">Ainda processando…</h1>
            <p className="mt-3 text-sm text-slate-400">
              Seu pagamento está sendo confirmado pelo Stripe. Isso pode levar alguns minutos.
              Você receberá a ativação em breve por e-mail.
            </p>
            <button
              data-testid="success-back-home"
              onClick={() => navigate("/")}
              className="mt-8 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold rounded-xl text-sm uppercase tracking-wider"
            >
              Voltar ao painel
            </button>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="payment-status-failed" className="rounded-2xl bg-[#0d1420] border border-red-700/40 p-10">
            <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-400" strokeWidth={2} />
            </div>
            <h1 className="mt-6 text-2xl font-serif font-bold text-white">Não localizamos sua transação</h1>
            <p className="mt-3 text-sm text-slate-400">
              Se você acabou de pagar, aguarde alguns minutos. Caso o problema persista, volte à página de planos e tente novamente.
            </p>
            <button
              data-testid="success-back-pricing"
              onClick={() => navigate("/pricing")}
              className="mt-8 px-6 py-3 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold rounded-xl text-sm uppercase tracking-wider"
            >
              Voltar aos planos
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
